<!-- Component can be used in microscope and periscope -->
<template>
  <div class="conversation-list-item" :class="{ active }" @click="$emit('click')">
    <div class="item-header">
      <span class="item-subject">{{ conversation.subject }}</span>
      <span class="item-badge" v-if="conversation.hasUnreadMessages" />
    </div>
    <div class="item-meta">
      <span class="item-status" :class="conversation.status">{{ statusLabel }}</span>
    </div>
    <p class="item-message" v-if="conversation.latestMessage">
      {{ truncatedMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ConversationListItem } from '@staff/shared'

const props = defineProps<{
  conversation: ConversationListItem
  active?: boolean
  maxMessageLength?: number
}>()

defineEmits<{
  (e: 'click'): void
}>()

const statusLabel = computed(() => (props.conversation.status === 'open' ? 'Öppen' : 'Stängd'))

const truncatedMessage = computed(() => {
  const msg = props.conversation.latestMessage?.message ?? ''
  const max = props.maxMessageLength ?? 80
  if (msg.length <= max) return msg
  return msg.slice(0, max) + '…'
})
</script>

<style scoped lang="scss">
.conversation-list-item {
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  background-color: var(--color-canvas-inset);
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:hover {
    background-color: var(--color-canvas-inset-prominent);
  }

  &.active {
    background-color: var(--color-canvas-inset-prominent);
  }
}

.item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.item-subject {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--color-fg-default);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-badge {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--color-danger-emphasis);
}

.item-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.item-status {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.6875rem;

  &.open {
    background-color: var(--color-success-subtle);
    color: var(--color-success-fg);
  }

  &.closed {
    background-color: var(--color-canvas-subtle);
    color: var(--color-fg-subtle);
  }
}

.item-message {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--color-fg-subtle);
  line-height: 1.4;
}
</style>
